import { Component, Inject, OnInit } from "@angular/core";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Tools } from "@comunicare/comunicare-helper";
import { IAddPatientData, IIdentifier, IIdentity, IPostPatientAdditionalData, Reference } from "@comunicare/comunicare-interfaces";
import { ChooseInsuranceDialogComponent } from "./choose-insurance-dialog/choose-insurance-dialog.component";

@Component({
  selector: "app-patient-already-exists-dialog",
  templateUrl: "./patient-already-exists-dialog.component.html",
  styleUrls: ["./patient-already-exists-dialog.component.scss"],
})
export class PatientAlreadyExistsDialogComponent implements OnInit {
  public isOtherOrg: boolean;
  public userOtherOrg: {
    mailAlreadyUsed?: boolean;
    wrongMail?: boolean;
    verifyNamesAndSexe?: boolean;
  };
  public isPractitioner: boolean;
  public usersFound: {
    sameEmail: boolean;
    isRelated: boolean;
    existingPatient: {
      birthDate?: string;
      firstname?: string;
      name?: string;
      mail?: string;
      phone?: string;
      healthcareService?: Reference[];
      insurance?: string;
      insuranceId?: string;
      requestChangeInsurance?: boolean;
      identity?: IIdentity[];
      uniqueIds?: IIdentifier[];
      requestChangeAddress?: boolean;
    };
    isSelected?: boolean;
  }[] = [];
  public indexSelected = 0;
  public relatedRegistration = false;

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<PatientAlreadyExistsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IAddPatientData & { additionalData: IPostPatientAdditionalData[] }
  ) {}

  ngOnInit(): void {
    const additionalData = this.data.additionalData;
    this.isPractitioner = additionalData[0].isPractitioner;
    const isSameOrg = additionalData.findIndex((ad) => ad.sameOrganization === true) !== -1;
    if (isSameOrg) {
      additionalData.forEach((user) => {
        if (user.sameOrganization || user.isRelated) {
          this.usersFound.push({
            existingPatient: user.existingPatient,
            isRelated: user.isRelated,
            sameEmail: this.isSameEmail(user.existingPatient.mail),
          });
        }
      });
      this.usersFound[this.indexSelected].isSelected = true;
    } else {
      const isOtherOrg = additionalData.findIndex((ad) => !ad.sameOrganization && !ad.isRelated) !== -1;
      if (isOtherOrg) {
        const otherOrgPatients = additionalData.filter((ad) => !ad.sameOrganization && !ad.isRelated);
        if (otherOrgPatients?.length > 1) {
          // multiple users found in differents irg, mail or identifier already used
          this.userOtherOrg = {
            mailAlreadyUsed: true,
          };
        } else {
          // one user found in other org
          this.userOtherOrg = {
            mailAlreadyUsed: otherOrgPatients[0].mailAlreadyUsed,
            wrongMail: otherOrgPatients[0].wrongMail,
            verifyNamesAndSexe: otherOrgPatients[0].verifyNamesAndSexe,
          };
        }
      } else {
        const isRelated = additionalData.findIndex((ad) => ad.isRelated === true) !== -1;
        if (isRelated) {
          this.usersFound.push({
            existingPatient: additionalData[0].existingPatient,
            isRelated: additionalData[0].isRelated,
            sameEmail: this.isSameEmail(additionalData[0].existingPatient.mail),
          });
        }
      }
    }
    this.isOtherOrg = Tools.isDefined(this.userOtherOrg);
    this.relatedRegistration = additionalData[0]?.relatedRegistration;
  }

  /**
   * The comparison must be case-insensitive
   */
  private isSameEmail(existingMail: string): boolean {
    return existingMail?.toLowerCase() === this.data.mail?.toLowerCase();
  }

  public validate(): void {
    if (
      this.usersFound[this.indexSelected].existingPatient.requestChangeInsurance ||
      this.usersFound[this.indexSelected].existingPatient.requestChangeAddress
    ) {
      // need to choose the insurance and/or address
      const dialogRef = this.dialog.open(ChooseInsuranceDialogComponent, {
        disableClose: true,
        data: {
          oldIns: this.usersFound[this.indexSelected].existingPatient.requestChangeInsurance
            ? this.usersFound[this.indexSelected].existingPatient.insurance
            : undefined,
          newIns: this.usersFound[this.indexSelected].existingPatient.requestChangeInsurance ? this.data.insuranceRef?.display : undefined,
          updateAddress: this.usersFound[this.indexSelected].existingPatient.requestChangeAddress,
        },
        panelClass: "dialog-container-scroll",
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.dialogRef.close({
            forceUpdateUser: this.usersFound[this.indexSelected].existingPatient.mail,
            needChangeInsurance: result.needChangeInsurance,
            needChangeAddress: result.needChangeAddress,
          });
        }
      });
    } else {
      this.dialogRef.close({
        forceUpdateUser: this.usersFound[this.indexSelected].existingPatient.mail,
      });
    }
  }

  public closeAll() {
    this.dialog.closeAll();
  }

  public sendMail(relatedRegistration = false) {
    const mailText = relatedRegistration
      ? "mailto:contact@comunicare.be?subject=Referent already exists"
      : "mailto:contact@comunicare.be?subject=Patient already exists in a different organization"; // add the links to body
    window.location.href = mailText;
  }
}
